import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import SEO from "../components/SEO";
import { galleryData } from "../staticData";
import { 
  ArrowRight,
  Eye,
  ArrowLeft,
  ArrowRightIcon
} from "lucide-react";

const Gallery = () => {
  // Use static data instead of API
  const loading = false;
  const error = null;

  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBefore, setShowBefore] = useState(true);

  const openModal = (item, index, isBefore = true) => {
    setSelectedImage(item);
    setCurrentIndex(index);
    setShowBefore(isBefore);
  };

  const nextImage = () => {
    if (!galleryData || galleryData.length === 0) return;
    const nextIndex = (currentIndex + 1) % galleryData.length;
    setSelectedImage(galleryData[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const prevImage = () => {
    if (!galleryData || galleryData.length === 0) return;
    const prevIndex = currentIndex === 0 ? galleryData.length - 1 : currentIndex - 1;
    setSelectedImage(galleryData[prevIndex]);
    setCurrentIndex(prevIndex);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Gallery - WOW AUTOBODY | Before & After Auto Body Repair Photos"
        description="View our gallery of before and after auto body repair transformations. See real examples of collision repair, dent removal, and auto painting work."
        keywords="auto body repair gallery, before after car repair, collision repair photos, dent removal results, auto painting examples"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-red-600 text-white mb-6 px-4 py-2">
            Before & After Gallery
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Our Work</h1>
          <p className="text-xl leading-relaxed">
            See the incredible transformations we've achieved for our customers. 
            From minor repairs to complete restorations, quality speaks for itself.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Before & After Transformations</h2>
            <p className="text-lg text-gray-600">Click on any image to view the full transformation</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryData && galleryData.map((item, index) => (
              <Card key={item.id} className="overflow-hidden border-2 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-0">
                  {/* Before Image */}
                  <div className="relative group">
                    <img 
                      src={item.before_image} 
                      alt={`${item.title} - Before`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => openModal(item, index, true)}
                            className="bg-white text-gray-900 hover:bg-gray-100"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Before
                          </Button>
                        </DialogTrigger>
                      </Dialog>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-red-600 text-white">
                      Before
                    </Badge>
                  </div>

                  {/* After Image */}
                  <div className="relative group">
                    <img 
                      src={item.after_image} 
                      alt={`${item.title} - After`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => openModal(item, index, false)}
                            className="bg-white text-gray-900 hover:bg-gray-100"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View After
                          </Button>
                        </DialogTrigger>
                      </Dialog>
                    </div>
                    <Badge className="absolute top-3 left-3 bg-green-600 text-white">
                      After
                    </Badge>
                  </div>

                  {/* Card Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    {item.category && (
                      <Badge className="mt-3 bg-blue-100 text-blue-800">
                        {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Modal for viewing images */}
          {selectedImage && (
            <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
              <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
                <div className="bg-white rounded-lg overflow-hidden">
                  <div className="relative">
                    <img 
                      src={showBefore ? selectedImage.before_image : selectedImage.after_image}
                      alt={`${selectedImage.title} - ${showBefore ? 'Before' : 'After'}`}
                      className="w-full h-96 object-cover"
                    />
                    
                    {/* Navigation buttons */}
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100"
                      onClick={prevImage}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="icon"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100"
                      onClick={nextImage}
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </Button>

                    {/* Before/After toggle */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                      <div className="flex bg-white rounded-lg p-1">
                        <Button
                          size="sm"
                          variant={showBefore ? "default" : "ghost"}
                          onClick={() => setShowBefore(true)}
                          className={showBefore ? "bg-red-600 text-white" : ""}
                        >
                          Before
                        </Button>
                        <Button
                          size="sm"
                          variant={!showBefore ? "default" : "ghost"}
                          onClick={() => setShowBefore(false)}
                          className={!showBefore ? "bg-green-600 text-white" : ""}
                        >
                          After
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedImage.title}</h3>
                    <p className="text-gray-600">{selectedImage.description}</p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Ready for Your Transformation?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let us restore your vehicle to its original beauty. Get a free estimate today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold">
                Get Free Estimate
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-semibold">
                View Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;